/*
 * What the step cards, the stepper, the hero and the env tiles decide before drawing: which tone a marker has, whether a
 * card starts collapsed, what the "Next" chip says, and what an environment tile reports. Pure (no React).
 */
import type { ScopeSelection } from '@/features/env/ScopePicker';
import { NEXT_ACTION, statusLabel, type Progress, type StepId, type StepState, type StepStatus } from './steps';
import type { EnvTile } from './setupModel';

/** The round marker of a step: done, attention, the current one (filled) or any other. */
export type MarkerTone = 'done' | 'attention' | 'current' | 'idle';

export function markerTone(status: StepStatus, current: boolean): MarkerTone {
  if (status === 'done') return 'done';
  if (status === 'attention') return 'attention';
  return current ? 'current' : 'idle';
}

/** On phones a done, blocked or checking step collapses until the person opens it; the current one stays open. */
export const collapsedByDefault = (status: StepStatus, current: boolean): boolean => !current && (status === 'done' || status === 'blocked' || status === 'checking');

/** The mono status text of a stepper row: "Next" for the current step still to do, else its status label. */
export const stepperStatusText = (s: StepState, current: boolean): string => (current && s.status === 'todo' ? 'Next' : statusLabel(s));

/** The step "Next" points at and what it asks for (its fix when it needs attention), or undefined when all is done. */
export function nextTarget(states: StepState[], progress: Progress): { id: StepId; label: string } | undefined {
  const next = progress.next ? states.find((s) => s.id === progress.next) : undefined;
  return next ? { id: next.id, label: NEXT_ACTION[next.id][next.status === 'attention' ? 'attention' : 'todo'] } : undefined;
}

/** While a fact is still loading the count, the bar and the data attributes wait (no 4 → 7 jump as answers arrive). */
export function heroView(progress: Progress, pending: boolean) {
  return {
    done: pending ? undefined : progress.done,
    next: pending ? undefined : progress.next,
    barValue: pending ? 0 : progress.percent,
    barLabel: pending ? 'Checking your setup' : `${progress.done} of ${progress.tracked} steps done`,
    percentText: pending ? '…' : `${progress.percent}%`,
  };
}

/** The scope the env sheets open on: this app in an environment (the first one when none is named). */
export const envScope = (app: { slug: string } | undefined, environments: Array<{ slug: string }>, environment?: string): ScopeSelection => ({ scope: 'app-env', appSlug: app?.slug, environment: environment ?? environments[0]?.slug });

/** What an environment tile reports under its key count: how many required keys are missing, or whether it resolved. */
export function tileStatus(tile: Pick<EnvTile, 'resolution' | 'error'>, missing: number): { missing: number } | { text: 'not resolved' | 'resolved' } {
  if (missing > 0) return { missing };
  return { text: tile.error && !tile.resolution ? 'not resolved' : 'resolved' };
}

/** The key count of a tile: still loading, read, or failed. */
export const tileKeysView = (tile: Pick<EnvTile, 'loading' | 'resolution'>): 'loading' | 'keys' | 'failed' => {
  if (tile.loading && !tile.resolution) return 'loading';
  return tile.resolution ? 'keys' : 'failed';
};
